import InputDate from "../../../component/form/input-date/InputDate";
import InputField from "../../../component/form/input-field/InputField";
import "./DonatePage.scss";
import InputTextarea from "../../../component/form/input-textarea/InputTextarea";
import InputFiles from "../../../component/form/input-files/InputFiles";
import Button from "../../../component/button/Button";
import React, { useState, useEffect } from "react";

const itemOptions = [
  { value: "furniture", label: "Furniture" },
  { value: "electronics", label: "Electronics" },
  { value: "large-appliances", label: "Large appliances" },
  { value: "clothing", label: "Clothing" },
  { value: "toys", label: "Toys" },
  { value: "books", label: "Books" },
  { value: "other", label: "Other - details in additional information" },
];

const stateOptions = [
  { value: "good-working-condition", label: "Good working condition" },
  { value: "visible-usage", label: "Visible usage" },
  { value: "damage", label: "Damage" },
  { value: "does-not-work", label: "Does not work" },
];

const upholsteryOptions = [
  { value: "fire-label", label: "Yes, with fire safety label" },
  { value: "no-fire-label", label: "Yes, but without fire safety label" },
  { value: "none", label: "No" },
];

const countOptions = Array.from({ length: 10 }, (_, i) => ({
  value: (i + 1).toString(),
  label: (i + 1).toString()
}));

const extendedCountOptions = [...countOptions, { value: "Other", label: "Other" }];

interface DonatePageUIProps {
  nameOfBuilding: string;
  contactName: string;
  email: string;
  pickupAddress: string;
  postcode: string;
  temporaryParkingSpace: string;
  availableDateFrom: Date | null;
  availableDateTo: Date | null;
  itemsTable: string;
  itemsAvailable: string;
  attachments: File[];
  submissionInProgress: boolean;
  isSuccess: boolean;
  submitMsg?: string;

  invalidNameOfBuilding?: string;
  invalidContactName?: string;
  invalidEmail?: string;
  invalidPickupAddress?: string;
  invalidPostcode?: string;
  invalidTemporaryParkingSpace?: string;
  invalidAvailableDateFrom?: string;
  invalidAvailableDateTo?: string;
  invalidItemsAvailable?: string;
  invalidAttachments?: string;

  setNameOfBuilding: (name: string) => void;
  setContactName: (name: string) => void;
  setEmail: (email: string) => void;
  setPickupAddress: (address: string) => void;
  setPostcode: (postcode: string) => void;
  setTemporaryParkingSpace: (items: string) => void;
  setAvailableDateFrom: (date: Date | null) => void;
  setAvailableDateTo: (date: Date | null) => void;
  setItemsTable: (items: string) => void;
  setItemsAvailable: (items: string) => void;
  handleFileChange: (files: File[]) => void;
  resetInvalidMsg: () => void;
  onSubmit: () => void;
  onCancel: () => void;

  darkMode: boolean;
}

const DonatePageUI = (props: DonatePageUIProps) => {

  const [rows, setRows] = useState([
    {
      item: "",
      state: "",
      quantity: "",
      dimensions: "",
      weight: "",
      upholstered: "",
    }
  ]);

  // Handle all other field changes
  const handleChange = (idx: any, field: any, value: any) => {
    setRows(rows =>
      rows.map((row, i) =>
        i === idx ? { ...row, [field]: value } : row
      )
    );
  };

  const handleAddRow = () => {
    setRows([
      ...rows,
      {
        item: "",
        state: "",
        quantity: "",
        dimensions: "",
        weight: "",
        upholstered: "",
      },
    ]);
  };

  const handleRemoveRow = (idx: number) => {
  setRows(rows => rows.length > 1 ? rows.filter((_, i) => i !== idx) : rows);
};

  // Generate concatenated string for all fully selected/entered rows
  useEffect(() => {
    const val = rows
      .filter(row => row.item) // Only include rows where Item is selected
      .map(row =>
        [
          `Item: ${row.item}`,
          `State: ${row.state}`,
          `Quantity: ${row.quantity}`,
          `Dimensions: ${row.dimensions}`,
          `Weight (kg): ${row.weight}`,
          `Upholstered: ${upholsteryOptions.find(u => u.value === row.upholstered)?.label || ""}`
        ].join(", ")
      )
      .join("\n");
    props.setItemsTable(val);
  }, [rows, props.setItemsTable]);

  return (
    <div className={"donate-wrapper" + (props.darkMode ? " dark" : "")}>
      <div className="banner-container">
        <img 
          style={{width:"100%", height:"auto", marginBottom:"20px"}} 
          src={props.darkMode ? String(`${process.env.REACT_APP_DONATION_BANNER_DARK_URL}`) : String(`${process.env.REACT_APP_DONATION_BANNER_LIGHT_URL}`)} 
          alt="Donate Now" 
        />
      </div>
      <h1 className="page-title">Donate Now</h1>
        <p className="page-subtitle">The information provided will only be used to collect the item listed, this will not affect any of your other services.</p>
      <div className="donate-form">
        <InputField
          label="Name of building *"
          value={props.nameOfBuilding}
          type="text"
          invalidMsg={props.invalidNameOfBuilding}
          onChange={(val) => {
            props.resetInvalidMsg();
            props.setNameOfBuilding(val);
          }}
          darkMode={props.darkMode}
        />
        <InputField
          label="Name of onsite contact for collection *"
          value={props.contactName}
          type="text"
          invalidMsg={props.invalidContactName}
          onChange={(val) => {
            props.resetInvalidMsg();
            props.setContactName(val);
          }}
          darkMode={props.darkMode}
        />
        <InputField
          label="Email of onsite contact for collection *"
          value={props.email}
          type="email"
          invalidMsg={props.invalidEmail}
          onChange={(val) => {
            props.resetInvalidMsg();
            props.setEmail(val);
          }}
          darkMode={props.darkMode}
        />
        <InputField
          label="Address for Pickup *"
          value={props.pickupAddress}
          type="text"
          invalidMsg={props.invalidPickupAddress}
          onChange={(val) => {
            props.resetInvalidMsg();
            props.setPickupAddress(val);
          }}
          darkMode={props.darkMode}
        />
        <InputField
          label="Postcode *"
          value={props.postcode}
          type="text"
          invalidMsg={props.invalidPostcode}
          onChange={(val) => {
            props.resetInvalidMsg();
            props.setPostcode(val.toUpperCase());
          }}
          darkMode={props.darkMode}
        />
        <InputField
          label="Temporary parking space available *"
          value={props.temporaryParkingSpace}
          type="text"
          invalidMsg={props.invalidTemporaryParkingSpace}
          onChange={(val) => {
            props.resetInvalidMsg();
            props.setTemporaryParkingSpace(val);
          }}
          darkMode={props.darkMode}
        />
        <InputDate
          label="When can the item be collected from *"
          value={props.availableDateFrom}
          invalidMsg={props.invalidAvailableDateFrom}
          onChange={(val) => {
            props.resetInvalidMsg();
            props.setAvailableDateFrom(val);
          }}
          darkMode={props.darkMode}
        />
        <InputDate
          label="How long will the item be kept on site for *"
          value={props.availableDateTo}
          invalidMsg={props.invalidAvailableDateTo}
          onChange={(val) => {
            props.resetInvalidMsg();
            props.setAvailableDateTo(val);
          }}
          darkMode={props.darkMode}
        />
      </div>

    <div style={{ marginBottom: "1.5rem", marginTop: "1.5rem" }}>
      {/* <textarea
        value={props.itemsTable}
        onChange={e => {
          props.resetInvalidMsg();
          props.setItemsTable(e.target.value);
        }}
        rows={Math.max(rows.length, 3)}
        style={{ width: "100%", marginBottom: "1rem" }}
        readOnly // Remove readOnly for manual editing
      /> */}
      <table className={"items-table" + (props.darkMode ? " dark" : "")} style={{ width: "100%", borderCollapse: "collapse", color: "white" }}>
        {/* <thead>
          <tr>
            <th style={{ textAlign: "left", padding: "8px" }}>Item *</th>
            <th style={{ textAlign: "left", padding: "8px" }}>State</th>
            <th style={{ textAlign: "left", padding: "8px" }}>Quantity</th>
            <th style={{ textAlign: "left", padding: "8px" }}>Dimensions</th>
            <th style={{ textAlign: "left", padding: "8px" }}>Weight (kg)</th>
            <th style={{ textAlign: "left", padding: "8px" }}>Upholstered</th>
            <th style={{ width: "42px" }}></th>
          </tr>
        </thead> */}
        <div style={{ padding: "8px", color: "white", fontSize: "1rem" }}>Item Details</div>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx}>
              {/* <td style={{ padding: "8px", color: "white", fontSize: "1rem" }}>
                Item Details
              </td> */}
              <td style={{ padding: "8px" }}>
                <select
                  value={row.item}
                  required
                  onChange={e => handleChange(idx, "item", e.target.value)}
                  style={{ width: "100%", padding: "4px" }}
                >
                  <option value="">Item *</option>
                  {itemOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </td>
              <td style={{ padding: "8px" }}>
                <select
                  value={row.state}
                  onChange={e => handleChange(idx, "state", e.target.value)}
                  style={{ width: "100%", padding: "4px" }}
                >
                  <option value="">Condition</option>
                  {stateOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </td>
              <td style={{ padding: "8px" }}>
                <select
                  value={row.quantity}
                  onChange={e => handleChange(idx, "quantity", e.target.value)}
                  style={{ width: "100%", padding: "4px" }}
                  >
                <option value="">Quantity</option>
                  {extendedCountOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </td>
              <td style={{ padding: "8px" }}>
                <input
                  type="text"
                  value={row.dimensions}
                  onChange={e => handleChange(idx, "dimensions", e.target.value)}
                  style={{ width: "100%", padding: "4px" }}
                  placeholder="Estimated dimensions"
                />
              </td>
              <td style={{ padding: "8px" }}>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={row.weight}
                  onChange={e => handleChange(idx, "weight", e.target.value)}
                  style={{ width: "100%", padding: "4px" }}
                  placeholder="Estimated weight"
                />
              </td>
              <td style={{ padding: "8px" }}>
                <select
                  value={row.upholstered}
                  onChange={e => handleChange(idx, "upholstered", e.target.value)}
                  style={{ width: "100%", padding: "4px" }}
                >
                  <option value="">Upholstered (if applicable)</option>
                  {upholsteryOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </td>
            <td style={{ padding: "8px", whiteSpace: "nowrap" }}>
            {/* Remove button (show only if more than 1 row) */}
            {rows.length > 1 && (
              <button
                type="button"
                onClick={() => handleRemoveRow(idx)}
                style={{
                  border: "none",
                  background: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "white",
                  marginRight: "8px",
                  userSelect: "none",
                }}
                title="Remove Row"
              >
                &minus;
              </button>
            )}
            {/* Add button on last row */}
            {idx === rows.length - 1 && (
              <button
                type="button"
                onClick={handleAddRow}
                style={{
                  border: "none",
                  background: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "white",
                  userSelect: "none",
                }}
                title="Add Row"
              >
                +
              </button>
            )}
          </td>
              {/* <td style={{ padding: "8px" }}>
                {idx === rows.length - 1 && (
                  <button
                    type="button"
                    onClick={handleAddRow}
                    style={{
                      border: "none",
                      background: "none",
                      fontSize: "20px",
                      cursor: "pointer",
                      color: "#0275d8"
                    }}
                    title="Add Row"
                  >
                    +
                  </button>
                )}
              </td> */}
            </tr>
          ))}
        </tbody>
      </table>
    </div>

      <InputTextarea
        label="Additional information *"
        value={props.itemsAvailable}
        invalidMsg={props.invalidItemsAvailable}
        onChange={(val) => {
          props.resetInvalidMsg();
          props.setItemsAvailable(val);
        }}
        darkMode={props.darkMode}
      />
      <InputFiles
        label="Attachments"
        value={props.attachments}
        invalidMsg={props.invalidAttachments}
        onChange={(val) => {
          props.resetInvalidMsg();
          props.handleFileChange(val);
        }}
        darkMode={props.darkMode}
      />
      <div className="submit-wrapper">
        <div className={"info-box" + (props.darkMode ? " dark" : "")}>
          <p>* Required</p>
          <p>
            Your data will only be used to coordinate pickup. By submitting, you
            agree to our <a href="https://www.bywaters.co.uk/privacy-policy" target="_blank"><u>Privacy Policy</u></a>.
          </p>
        </div>
        <div className="buttons-panel">
          <div
            className={"cancel-button" + (props.darkMode ? " dark" : "")}
            onClick={props.onCancel}
          >
            Cancel
          </div>
          <Button
            title="Submit"
            className="submit-button"
            onClick={props.onSubmit}
            loading={props.submissionInProgress}
          />
        </div>
      </div>
      {props.submitMsg && (
        <div className={props.isSuccess ? "success-msg" : "error-msg"}>
          <span>{props.submitMsg}</span>
        </div>
      )}
    </div>
  );
};

export default DonatePageUI;
